import { LESSONS, type LessonId } from '../data/lessons'
import { getNextLesson, type Progress } from '../core/progress'
import { ArrowIcon, CheckIcon, LockIcon, PracticeIcon } from './Icons'

interface HomeViewProps {
  progress: Progress
  onStartLesson: (lessonId: LessonId) => void
}

function formatMinutes(milliseconds: number): string {
  const minutes = Math.round(milliseconds / 60_000)
  if (minutes < 1) return '0分'
  if (minutes < 60) return `${minutes}分`
  return `${Math.floor(minutes / 60)}時間${minutes % 60 ? `${minutes % 60}分` : ''}`
}

export function HomeView({ progress, onStartLesson }: HomeViewProps) {
  const nextLesson = getNextLesson(progress) ?? LESSONS[LESSONS.length - 1]
  const totalAccuracy = progress.totalAttempts
    ? Math.round((progress.totalCorrect / progress.totalAttempts) * 100)
    : 0
  const completed = LESSONS.filter(
    (lesson) => progress.lessons[lesson.id].status === 'completed',
  ).length
  const completion = Math.round((completed / LESSONS.length) * 100)

  return (
    <main className="home-view page-enter">
      <section className="hero-card" aria-labelledby="hero-title">
        <div className="hero-copy">
          <div className="eyebrow"><span className="eyebrow-dot" /> YOUR DAILY PRACTICE</div>
          <h1 id="hero-title">耳と指で、<br /><span>少しずつ。</span></h1>
          <p>ひとつ覚えたら、次のひとつへ。<br className="desktop-only" />うさぎのペースで続けよう。</p>
          <button className="primary-button" type="button" onClick={() => onStartLesson(nextLesson.id)}>
            <PracticeIcon size={19} />
            {progress.totalSessions ? 'つづきから練習' : '最初のレッスンを始める'}
            <ArrowIcon size={18} />
          </button>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="orbit orbit-one"><span>·</span><span>—</span><span>·</span></div>
          <div className="orbit orbit-two"><span>—</span><span>·</span></div>
          <div className="rabbit-scene">
            <div className="rabbit-ear rabbit-ear-left"><i /></div>
            <div className="rabbit-ear rabbit-ear-right"><i /></div>
            <div className="rabbit-head">
              <span className="rabbit-eye left" />
              <span className="rabbit-eye right" />
              <span className="rabbit-nose" />
              <span className="rabbit-mouth" />
            </div>
            <div className="rabbit-body"><span className="rabbit-paw left" /><span className="rabbit-paw right" /></div>
            <div className="key-base"><span /><span /></div>
          </div>
          <div className="sound-wave wave-left"><i /><i /><i /></div>
          <div className="sound-wave wave-right"><i /><i /></div>
        </div>

        <div className="hero-progress">
          <div className="progress-ring" style={{ '--progress': `${completion * 3.6}deg` } as React.CSSProperties}>
            <div><strong>{completion}%</strong><span>コース</span></div>
          </div>
          <div><span>NEXT</span><strong>LEVEL {nextLesson.level}</strong><small>{nextLesson.title}</small></div>
        </div>
      </section>

      <section className="stats-grid" aria-label="学習記録">
        <article className="stat-card">
          <span className="stat-icon mint">◎</span>
          <div><small>これまでの正答率</small><strong>{totalAccuracy}<em>%</em></strong></div>
          <span className="stat-note">{progress.totalAttempts || 'まだ記録なし'}{progress.totalAttempts ? ' 回入力' : ''}</span>
        </article>
        <article className="stat-card">
          <span className="stat-icon orange">↗</span>
          <div><small>練習セッション</small><strong>{progress.totalSessions}<em>回</em></strong></div>
          <span className="stat-note">積み重ねが力になる</span>
        </article>
        <article className="stat-card">
          <span className="stat-icon blue">◷</span>
          <div><small>練習した時間</small><strong className="time-stat">{formatMinutes(progress.totalDurationMs)}</strong></div>
          <span className="stat-note">端末内に自動保存</span>
        </article>
      </section>

      <section className="course-section" aria-labelledby="course-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">STEP BY STEP</span>
            <h2 id="course-title">学習コース</h2>
          </div>
          <p>合格すると、次のレベルがひらきます</p>
        </div>

        <div className="lesson-path">
          {LESSONS.map((lesson, index) => {
            const lessonProgress = progress.lessons[lesson.id]
            const locked = lessonProgress.status === 'locked'
            const complete = lessonProgress.status === 'completed'
            const accuracy = lessonProgress.attempts
              ? Math.round((lessonProgress.correct / lessonProgress.attempts) * 100)
              : 0

            return (
              <article
                className={`lesson-card ${locked ? 'is-locked' : ''} ${complete ? 'is-complete' : ''}`}
                key={lesson.id}
              >
                <div className="level-badge">
                  {complete ? <CheckIcon size={22} /> : locked ? <LockIcon size={19} /> : lesson.level}
                </div>
                <div className="lesson-copy">
                  <span>LEVEL {lesson.level}</span>
                  <h3>{lesson.title}</h3>
                  <p>{lesson.description}</p>
                  <div className="character-row" aria-label={`練習文字: ${lesson.newCharacters.join(' ')}`}>
                    {lesson.newCharacters.slice(0, 9).map((character) => <kbd key={character}>{character}</kbd>)}
                    {lesson.newCharacters.length > 9 && <kbd>+{lesson.newCharacters.length - 9}</kbd>}
                  </div>
                </div>
                <div className="lesson-action">
                  {complete && <span className="accuracy-chip">BEST {Math.round(lessonProgress.bestAccuracy * 100) || accuracy}%</span>}
                  {!locked ? (
                    <button type="button" aria-label={`${lesson.title}を練習`} onClick={() => onStartLesson(lesson.id)}>
                      <ArrowIcon size={20} />
                    </button>
                  ) : <span className="locked-label">LOCKED</span>}
                </div>
                {index < LESSONS.length - 1 && <span className="path-line" aria-hidden="true" />}
              </article>
            )
          })}
        </div>
      </section>
    </main>
  )
}
